import React, { useEffect, useState } from 'react';
import { requester } from 'App';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { filterDataByThisWeek, combineDuplicates } from 'routes/vendorRe/commonFn/vendorPurchaseOrder';
import dashboardStyles from 'routes/vendor/dashboard/VendorNewDashboard.module.css';
import styles from './VendorSale.module.css';
import Dummy from 'components/Dummy';

const VendorSale = () => {
    const { storeId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [isMounted, setMounted] = useState(false);
    const [saleData, setSaleData] = useState([]);
    const [storeName, setStoreName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [sortOption, setSortOption] = useState('quantity'); // 기본 정렬: 판매량

    // 페이지네이션 상태
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20); // 한 페이지당 20개 항목

    // 검색 상태
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (location.state && location.state.storeName) {
            setStoreName(location.state.storeName);
        }
    }, [location]);

    useEffect(() => {
        if (isMounted) {
            setIsLoading(true);

            if (storeId) {
                let data = { id: storeId };
                requester.findAllOrderProductsByStoreId(data, (result) => {
                    // 오늘 자 기준, [월~일] 주간의 데이터 가져오기
                    const filteredData = filterDataByThisWeek(result.data);
                    // 여러 주문 중에 중복되는 아이템 합치기
                    const combinedSaleData = combineDuplicates(filteredData);
                    setSaleData(combinedSaleData);
                    setSearchResults(combinedSaleData);
                    setIsLoading(false);
                });
            } else {
                requester.findAllVendorPurchaseOrder((result) => {
                    // 오늘 자 기준, [월~일] 주간의 데이터 가져오기
                    const filteredData = filterDataByThisWeek(result.data);
                    // 여러 주문 중에 중복되는 아이템 합치기
                    const combinedSaleData = combineDuplicates(filteredData);
                    setSaleData(combinedSaleData);
                    setSearchResults(combinedSaleData);
                    setIsLoading(false);
                });
            }
        }
    }, [isMounted, storeId]);

    // 정렬 함수
    const sortData = (data, option) => {
        if (!data) return [];

        const sortedData = [...data];

        switch (option) {
            case 'quantity':
                return sortedData.sort((a, b) => b.quantity - a.quantity);
            case 'price':
                return sortedData.sort((a, b) => (b.price * b.quantity) - (a.price * a.quantity));
            case 'name':
                return sortedData.sort((a, b) => a.productName.localeCompare(b.productName));
            default:
                return sortedData;
        }
    };

    // 정렬 옵션 변경 핸들러
    const handleSortChange = (option) => {
        setSortOption(option);
        setCurrentPage(1); // 정렬 변경 시 첫 페이지로 이동
    };

    // 검색 핸들러
    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);

        if (term.trim() === '') {
            setSearchResults(saleData);
        } else {
            const filtered = saleData.filter(product =>
                product.productName.toLowerCase().includes(term.toLowerCase())
            );
            setSearchResults(filtered);
        }

        setCurrentPage(1); // 검색 시 첫 페이지로 이동
    };

    // 페이지 변경 핸들러
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // 페이지당 항목 수 변경 핸들러
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1); // 페이지당 항목 수 변경 시 첫 페이지로 이동
    };

    // 정렬된 데이터
    const sortedData = sortData(searchResults, sortOption);

    // 현재 페이지의 데이터
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);

    // 총 페이지 수 계산
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    // 페이지네이션 버튼 생성
    const pageNumbers = [];
    const maxPageButtons = 5; // 최대 표시할 페이지 버튼 수

    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    if (endPage - startPage + 1 < maxPageButtons) {
        startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    return (
        <div className={dashboardStyles.container}>
            <div className={styles.saleContainer}>
                <div className={styles.header}>
                    <div className={styles.titleSection}>
                        <h1 className={styles.title}>{storeName ? storeName : "전 매장"} 판매 내역</h1>
                        <p className={styles.subtitle}>이번 주 판매량 기준</p>
                    </div>
                    <div className={styles.actions}>
                        <button
                            className={styles.backButton}
                            onClick={() => navigate(-1)}
                        >
                            &larr; 돌아가기
                        </button>
                    </div>
                </div>

                <div className={dashboardStyles.card}>
                    <div className={dashboardStyles.section}>
                        <div className={dashboardStyles.sectionTitle}>
                            <div className={dashboardStyles.statsHeader}>
                                <span>판매량 BEST</span>
                                <div className={styles.sortOptions}>
                                    <button
                                        className={`${styles.sortButton} ${sortOption === 'quantity' ? styles.active : ''}`}
                                        onClick={() => handleSortChange('quantity')}
                                    >
                                        판매량순
                                    </button>
                                    <button
                                        className={`${styles.sortButton} ${sortOption === 'price' ? styles.active : ''}`}
                                        onClick={() => handleSortChange('price')}
                                    >
                                        매출액순
                                    </button>
                                    <button
                                        className={`${styles.sortButton} ${sortOption === 'name' ? styles.active : ''}`}
                                        onClick={() => handleSortChange('name')}
                                    >
                                        이름순
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={styles.filterSection}>
                            <div className={styles.searchBox}>
                                <input
                                    type="text"
                                    placeholder="상품명 검색..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    className={styles.searchInput}
                                />
                            </div>
                            <Dummy width={"15px"} />
                            <div className={styles.itemsPerPageSelector}>
                                <label htmlFor="itemsPerPage">페이지당 항목:</label>
                                <select
                                    id="itemsPerPage"
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                    className={styles.selectInput}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.resultsInfo}>
                            총 {sortedData.length}개 상품 중 {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedData.length)}개 표시
                        </div>

                        <div className={dashboardStyles.sectionContent}>
                            {isLoading ? (
                                <div className={dashboardStyles.loading}>데이터를 불러오는 중...</div>
                            ) : currentItems.length > 0 ? (
                                <div className={styles.productGrid}>
                                    {currentItems.map((product, index) => (
                                        <div key={index} className={styles.productCard}>
                                            <div className={styles.productRank}>
                                                {sortOption === 'quantity' && (indexOfFirstItem + index < 3) && (
                                                    <div className={styles.rankBadge}>{indexOfFirstItem + index + 1}</div>
                                                )}
                                            </div>
                                            <div className={styles.productImageContainer}>
                                                {product.productImg ? (
                                                    <img
                                                        src={product.productImg}
                                                        alt={product.productName}
                                                        className={styles.productImage}
                                                    />
                                                ) : (
                                                    <div className={styles.noImage}>이미지 없음</div>
                                                )}
                                            </div>
                                            <div className={styles.productInfo}>
                                                <h3 className={styles.productName}>{product.productName}</h3>
                                                <div className={styles.productStats}>
                                                    <div className={styles.statItem}>
                                                        <span className={styles.statLabel}>판매량</span>
                                                        <span className={styles.statValue}>{product.quantity}개</span>
                                                    </div>
                                                    {product.price && (
                                                        <div className={styles.statItem}>
                                                            <span className={styles.statLabel}>매출액</span>
                                                            <span className={styles.statValue}>
                                                                {(product.price * product.quantity).toLocaleString()}원
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className={dashboardStyles.noData}>
                                    {searchTerm ? '검색 결과가 없습니다.' : '판매 데이터가 없습니다.'}
                                </div>
                            )}

                            {/* 페이지네이션 */}
                            {totalPages > 1 && (
                                <div className={styles.pagination}>
                                    <button
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage === 1}
                                        className={styles.pageButton}
                                    >
                                        &laquo;
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={styles.pageButton}
                                    >
                                        &lt;
                                    </button>

                                    {pageNumbers.map(number => (
                                        <button
                                            key={number}
                                            onClick={() => handlePageChange(number)}
                                            className={`${styles.pageButton} ${currentPage === number ? styles.activePage : ''}`}
                                        >
                                            {number}
                                        </button>
                                    ))}

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={styles.pageButton}
                                    >
                                        &gt;
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(totalPages)}
                                        disabled={currentPage === totalPages}
                                        className={styles.pageButton}
                                    >
                                        &raquo;
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VendorSale;